import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Activities.svg";
import WhatsAppButton from "../components/WhatsappButton";
import { Link } from "react-router-dom";

const Activities: React.FC = () => {
  const { t } = useLanguage();

  const sensoryActivities = [
    {
      title: t("activities.sensoryActivities.naturalMaterials.title"),
      description: t(
        "activities.sensoryActivities.naturalMaterials.description"
      ),
      icon: "🌊",
      color: "bg-blue-500",
    },
    {
      title: t("activities.sensoryActivities.outdoorElements.title"),
      description: t(
        "activities.sensoryActivities.outdoorElements.description"
      ),
      icon: "🍃",
      color: "bg-green-500",
    },
    {
      title: t("activities.sensoryActivities.creativeMaterials.title"),
      description: t(
        "activities.sensoryActivities.creativeMaterials.description"
      ),
      icon: "🎨",
      color: "bg-purple-500",
    },
  ];

  const sensoryBenefits = [
    t("activities.sensoryBenefits.exploringTextures"),
    t("activities.sensoryBenefits.developMotorSkills"),
    t("activities.sensoryBenefits.emotionRegulation"),
    t("activities.sensoryBenefits.problemSolving"),
  ];

  const languageActivities = [
    {
      title: t("activities.languageActivities.dailyReading.title"),
      description: t("activities.languageActivities.dailyReading.description"),
      icon: "📚",
    },
    {
      title: t("activities.languageActivities.visualLearning.title"),
      description: t(
        "activities.languageActivities.visualLearning.description"
      ),
      icon: "🖼️",
    },
    {
      title: t("activities.languageActivities.musicRhymes.title"),
      description: t("activities.languageActivities.musicRhymes.description"),
      icon: "🎵",
    },
    {
      title: t("activities.languageActivities.rolePlay.title"),
      description: t("activities.languageActivities.rolePlay.description"),
      icon: "🎭",
    },
  ];

  const creativeActivities = [
    {
      title: t("activities.creativeActivities.artsCrafts.title"),
      description: t("activities.creativeActivities.artsCrafts.description"),
      icon: "🎨",
      color: "bg-red-500",
    },
    {
      title: t("activities.creativeActivities.buildingConstruction.title"),
      description: t(
        "activities.creativeActivities.buildingConstruction.description"
      ),
      icon: "🏗️",
      color: "bg-amber-500",
    },
    {
      title: t("activities.creativeActivities.musicDance.title"),
      description: t("activities.creativeActivities.musicDance.description"),
      icon: "💃",
      color: "bg-purple-500",
    },
    {
      title: t("activities.creativeActivities.drawingColoring.title"),
      description: t(
        "activities.creativeActivities.drawingColoring.description"
      ),
      icon: "✏️",
      color: "bg-green-500",
    },
  ];

  const thematicAreas = [
    {
      title: t("activities.thematicAreas.arithmetic.title"),
      description: t("activities.thematicAreas.arithmetic.description"),
      icon: "🔢",
      color: "bg-blue-500",
    },
    {
      title: t("activities.thematicAreas.language.title"),
      description: t("activities.thematicAreas.language.description"),
      icon: "💬",
      color: "bg-green-500",
    },
    {
      title: t("activities.thematicAreas.motorSkills.title"),
      description: t("activities.thematicAreas.motorSkills.description"),
      icon: "✂️",
      color: "bg-red-500",
    },
    {
      title: t("activities.thematicAreas.socialBehavior.title"),
      description: t("activities.thematicAreas.socialBehavior.description"),
      icon: "🤝",
      color: "bg-purple-500",
    },
  ];

  const bsoActivities = [
    {
      category: t("activities.bsoActivities.sportsExercise.category"),
      activities: [
        t("activities.bsoActivities.sportsExercise.activities.0"),
        t("activities.bsoActivities.sportsExercise.activities.1"),
      ],
      icon: "⚽",
      color: "bg-green-500",
    },
    {
      category: t("activities.bsoActivities.technologyExperiments.category"),
      activities: [
        t("activities.bsoActivities.technologyExperiments.activities.0"),
        t("activities.bsoActivities.technologyExperiments.activities.1"),
      ],
      icon: "🔬",
      color: "bg-blue-500",
    },
    {
      category: t("activities.bsoActivities.cookingBaking.category"),
      activities: [
        t("activities.bsoActivities.cookingBaking.activities.0"),
        t("activities.bsoActivities.cookingBaking.activities.1"),
        t("activities.bsoActivities.cookingBaking.activities.2"),
      ],
      icon: "👨‍🍳",
      color: "bg-amber-500",
    },
    {
      category: t("activities.bsoActivities.excursionsTrips.category"),
      activities: [
        t("activities.bsoActivities.excursionsTrips.activities.0"),
        t("activities.bsoActivities.excursionsTrips.activities.1"),
      ],
      icon: "🗺️",
      color: "bg-purple-500",
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[280px] lg:max-w-[480px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {t("activities.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-center text-natural-800 opacity-90 max-w-4xl mx-auto leading-relaxed">
              {t("activities.hero.subtitle")}
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 md:p-12 mb-16">
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold text-black mb-6 leading-tight">
                  {t("activities.intro.title")}
                </h2>
                <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                  {t("activities.intro.p1")}
                </p>
                <p className="text-lg text-neutral-800 leading-relaxed">
                  {t("activities.intro.p2")}
                </p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-8 border border-amber-100 mt-6">
                <h3 className="text-2xl font-semibold text-black mb-6">
                  {t("activities.intro.ourApproachTitle")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("activities.intro.ourApproachText")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sensory Activities Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
              {t("activities.sensory.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("activities.sensory.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {sensoryActivities.map((activity, index) => (
                <div key={index} className="group">
                  <div
                    className={`${activity.color} rounded-2xl p-8 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                  >
                    <div className="text-5xl mb-6">{activity.icon}</div>
                    <h3 className="text-xl font-semibold mb-4">
                      {activity.title}
                    </h3>
                    <p className="text-lg opacity-90 leading-relaxed">
                      {activity.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-semibold text-black mb-6 text-center">
                {t("activities.sensory.helpsWithTitle")}
              </h3>
              <div className="text-center">
                {sensoryBenefits.map((benefit, index) => (
                  <div key={index} className="space-x-3">
                    <span className="text-neutral-800">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <p className="text-lg text-neutral-800">
                  {t("activities.sensory.reggioNote")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Language Development Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
              {t("activities.language.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("activities.language.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {languageActivities.map((activity, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 text-center"
                >
                  <div className="text-4xl mb-4">{activity.icon}</div>
                  <h3 className="text-lg font-semibold text-black mb-3">
                    {activity.title}
                  </h3>
                  <p className="text-sm text-neutral-800">
                    {activity.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="bg-[#FBEACD] rounded-3xl shadow-2xl p-8 text-black text-center">
              <p className="text-lg opacity-90">
                {t("activities.language.note")}
              </p>
            </div>
          </div>
        </div>

        {/* Creative Expression Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
              {t("activities.creative.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("activities.creative.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {creativeActivities.map((activity, index) => (
              <div key={index} className="group">
                <div
                  className={`${activity.color} rounded-2xl p-6 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                >
                  <div className="text-4xl mb-4">{activity.icon}</div>
                  <h3 className="text-lg font-semibold mb-3">
                    {activity.title}
                  </h3>
                  <p className="text-sm opacity-90">{activity.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-neutral-800 max-w-4xl mx-auto">
              {t("activities.creative.note")}
            </p>
          </div>
        </div>

        {/* Thematic Work Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
              {t("activities.thematic.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("activities.thematic.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {thematicAreas.map((area, index) => (
                <div key={index} className="group">
                  <div
                    className={`${area.color} rounded-2xl p-6 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                  >
                    <div className="text-4xl mb-4">{area.icon}</div>
                    <h3 className="text-lg font-semibold mb-3">{area.title}</h3>
                    <p className="text-sm opacity-90">{area.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 text-center">
              <p className="text-lg text-neutral-800">
                {t("activities.thematic.note")}
              </p>
            </div>
          </div>
        </div>

        {/* After-School Care Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
              {t("activities.bso.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("activities.bso.subtitle")}
            </p>
          </div>
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {bsoActivities.map((category, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className=" space-x-4 mb-6 flex flex-row items-center justify-center">
                    <div
                      className={`w-16 h-16 rounded-full ${category.color} flex items-center justify-center text-2xl text-white`}
                    >
                      {category.icon}
                    </div>
                    <h3 className="text-2xl font-semibold text-left text-black">
                      {category.category}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {category.activities.map((activity, idx) => (
                      <li key={idx} className="text-center space-x-3">
                        <span className="text-neutral-800">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <div className="bg-amber-50 rounded-3xl p-8 border border-amber-100">
                <p className="text-lg text-neutral-800">
                  {t("activities.bso.note")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 py-16 font-child">
          <div className="text-center">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-semibold text-neutral-900 mb-4">
                {t("activities.cta.title")}
              </h3>
              <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t("activities.cta.subtitle")}
              </p>
              <Link
                to="/daily-routine"
                className="bg-[#FBEACD] text-[#492A19] px-8 py-3 rounded-full font-semibold text-sm md:text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("activities.cta.button")}
              </Link>
            </div>
          </div>
        </div>
        {/* Footer Section */}
      </div>

      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
      {/* End of relative z-10 */}
    </div>
  );
};

export default Activities;
